// In the installed PWA (display: standalone) there are no tabs and no
// browser chrome: target="_blank" anchors and window.open() replace the
// current view and leave the user stranded with no back UI. Here we reroute
// those openings: internal URLs go through the SPA router, external http(s)
// URLs navigate in the same window — out-of-scope pages get browser chrome
// from the OS (Safari overlay with Done on iOS, Custom Tab with ✕ on
// Android) — and custom schemes (mailto:, tel:, …) are handed to the OS.
// In a regular browser nothing is installed and new tabs work as usual.

type Navigate = (path: string) => void;
let navigate: Navigate | null = null;

export function registerStandaloneNavigate(fn: Navigate): void {
  navigate = fn;
}

function isStandaloneDisplay(): boolean {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches === true ||
    (window.navigator as { standalone?: boolean }).standalone === true
  );
}

function reroute(url: URL): void {
  if (url.protocol !== "http:" && url.protocol !== "https:") {
    // mailto:, tel:, whatsapp:… — the OS intercepts these without
    // navigating the app
    window.location.href = url.href;
  } else if (url.origin === window.location.origin) {
    const path = url.pathname + url.search + url.hash;
    if (navigate) navigate(path);
    else window.location.assign(path);
  } else {
    window.location.assign(url.href);
  }
}

export function setupStandaloneLinkHandling(): void {
  if (!isStandaloneDisplay()) return;

  const originalOpen = window.open.bind(window);
  window.open = function (
    url?: string | URL,
    target?: string,
    features?: string
  ) {
    // Sized popups (OAuth flows relying on window.opener) keep the real
    // window.open; everything else gets rerouted
    if (features) return originalOpen(url, target, features);
    if (url) {
      try {
        reroute(new URL(String(url), window.location.href));
      } catch {
        return originalOpen(url, target, features);
      }
    }
    return null;
  } as typeof window.open;

  document.addEventListener("click", (event) => {
    if (event.defaultPrevented) return;
    const target = event.target as HTMLElement | null;
    const anchor = target?.closest?.('a[target="_blank"]');
    if (!(anchor instanceof HTMLAnchorElement)) return;
    if (anchor.hasAttribute("download") || !anchor.href) return;

    let url: URL;
    try {
      url = new URL(anchor.href, window.location.href);
    } catch {
      return;
    }
    if (url.protocol !== "http:" && url.protocol !== "https:") return;

    event.preventDefault();
    reroute(url);
  });
}
